<!-- src/views/auth/Login.vue -->
<!--
  La connexion, en deux temps.

    1. identifiants → le mot de passe ;
    2. mfa ou enroll → le second facteur, ou sa configuration s'il n'existe pas encore.

  🔴 La DESTINATION EN ATTENTE (`next-route.js`) traverse maintenant TROIS écrans au lieu de
  deux : connexion → second facteur → éventuel changement de mot de passe. Elle n'est
  consommée que par le tout dernier. C'est ce qui permet au parcours « je scanne le QR de ma
  carte, je me connecte, je rattache » de continuer à fonctionner : le code de rattachement
  vit dans le FRAGMENT de l'URL et n'existe nulle part ailleurs — le perdre oblige le client
  à ressortir son courriel.
-->
<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div>
        <img class="mx-auto h-16 w-auto" src="@/assets/images/logo.png" alt="iOr">
        <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
          UltraLINK
        </h2>
        <p class="mt-2 text-center text-sm text-gray-600">
          {{ etape === 'identifiants' ? $t('connexion.sousTitreIdentifiants')
             : etape === 'mfa' ? $t('connexion.sousTitreMfa')
             : $t('connexion.sousTitreEnrolement') }}
        </p>
        <div class="mt-3 flex justify-center">
          <langue-select />
        </div>
      </div>

      <!-- ════ ÉTAPE 1 — identifiants ═══════════════════════════════════════════════════ -->
      <template v-if="etape === 'identifiants'">
        <div class="flex justify-center space-x-4 mt-4">
          <button @click="userType = 'admin'"
            :class="['px-4 py-2 text-sm font-medium rounded-md',
                     userType === 'admin' ? 'bg-primary-100 text-primary-700 border border-primary-300'
                                          : 'text-gray-500 hover:text-gray-700']">
            {{ $t('connexion.ongletAdmin') }}
          </button>
          <button @click="userType = 'client'"
            :class="['px-4 py-2 text-sm font-medium rounded-md',
                     userType === 'client' ? 'bg-primary-100 text-primary-700 border border-primary-300'
                                           : 'text-gray-500 hover:text-gray-700']">
            {{ $t('connexion.ongletClient') }}
          </button>
        </div>

        <!-- Retours des liens cliqués dans un courriel : confirmation d'adresse, ou
             opposition à une demande de récupération. Le serveur redirige ici plutôt que de
             répondre du JSON à une messagerie. -->
        <div v-if="avisVerif" :class="avisVerif.ok ? 'bg-green-50 border-green-200 text-green-800'
                                                   : 'bg-amber-50 border-amber-200 text-amber-800'"
             class="rounded-md border p-3 text-sm">
          {{ $t(avisVerif.cle) }}
        </div>

        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <form class="mt-8 space-y-6" @submit.prevent="soumettreIdentifiants">
          <div class="rounded-md shadow-sm -space-y-px">
            <div v-if="userType === 'admin'">
              <label for="username" class="sr-only">{{ $t('connexion.identifiant') }}</label>
              <input id="username" name="username" type="text" v-model="form.username" required
                     autocomplete="username"
                     class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                     :placeholder="$t('connexion.identifiant')">
            </div>
            <div v-else>
              <label for="email" class="sr-only">{{ $t('connexion.email') }}</label>
              <input id="email" name="email" type="email" v-model="form.email" required
                     autocomplete="username"
                     class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                     :placeholder="$t('connexion.email')">
            </div>
            <div>
              <label for="password" class="sr-only">{{ $t('connexion.motDePasse') }}</label>
              <input id="password" name="password" type="password" v-model="form.password" required
                     autocomplete="current-password"
                     class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                     :placeholder="$t('connexion.motDePasse')">
            </div>
          </div>

          <div class="flex items-center justify-end">
            <div class="text-sm">
              <a href="#" @click.prevent="showForgotPassword = true" class="font-medium text-primary-600 hover:text-primary-500">
                {{ $t('connexion.oublie') }}
              </a>
            </div>
          </div>

          <div>
            <button type="submit" :disabled="chargement"
                    class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60">
              <span v-if="chargement" class="absolute left-0 inset-y-0 flex items-center pl-3">
                <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              {{ $t('connexion.seConnecter') }}
            </button>
          </div>

          <p v-if="userType === 'client'" class="text-center text-sm text-gray-500">
            {{ $t('connexion.nouvelAppareil') }}
            <router-link to="/signup" class="font-medium text-primary-600 hover:text-primary-500">
              {{ $t('connexion.creerCompte') }}
            </router-link>
          </p>
        </form>
      </template>

      <!-- ════ ÉTAPE 2 — le second facteur ══════════════════════════════════════════════ -->
      <template v-else-if="etape === 'mfa'">
        <div v-if="error" class="alert alert-danger">{{ error }}</div>

        <!-- Écran de récupération : affiché à la place du formulaire une fois la demande
             déposée, pour que l'utilisateur voie ce qui va se passer et quand. -->
        <div v-if="recuperation" class="space-y-4">
          <div class="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <h3 class="font-semibold">{{ $t('connexion.recupTitre') }}</h3>
            <i18n-t keypath="connexion.recupCourriel" tag="p" class="mt-2">
              <template #adresse><strong>{{ recuperation.adresseMasquee }}</strong></template>
            </i18n-t>
            <i18n-t keypath="connexion.recupEcheance" tag="p" class="mt-2">
              <template #echeance><strong>{{ recuperation.echeance }}</strong></template>
              <template #delai>{{ recuperation.delaiH }}</template>
              <template #sauf><em>{{ $t('connexion.recupSauf') }}</em></template>
            </i18n-t>
            <p class="mt-2">
              {{ $t('connexion.recupRien') }}
            </p>
            <p class="mt-2">
              {{ $t('connexion.recupPresse') }}
            </p>
          </div>
          <button @click="recommencer" class="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            {{ $t('connexion.retourConnexion') }}
          </button>
        </div>

        <div v-else class="space-y-5">
          <p class="text-sm text-gray-600">
            <template v-if="modeSecours">
              {{ $t('connexion.saisirSecours') }}
            </template>
            <template v-else-if="methode === 'email'">
              <template v-if="envoi && envoi.envoye">
                <i18n-t keypath="connexion.codeEnvoye">
                  <template #adresse><strong>{{ envoi.adresseMasquee }}</strong></template>
                  <template #minutes>{{ envoi.validiteMin }}</template>
                </i18n-t>
              </template>
              <span v-else class="text-amber-700">
                {{ $t('connexion.courrielEchec') }}
              </span>
            </template>
            <template v-else>
              {{ $t('connexion.saisirTotp') }}
            </template>
          </p>

          <input v-if="!modeSecours" v-model="code" inputmode="numeric" autocomplete="one-time-code"
                 maxlength="7" placeholder="123 456" ref="champCode" @keyup.enter="verifier"
                 class="block w-full rounded-md border border-gray-300 px-3 py-3 text-center font-mono text-2xl tracking-[0.3em] focus:border-primary-500 focus:ring-primary-500">
          <input v-else v-model="codeSecours" autocomplete="off" placeholder="XXXXX-XXXXX"
                 @keyup.enter="verifier"
                 class="block w-full rounded-md border border-gray-300 px-3 py-3 text-center font-mono text-lg tracking-widest uppercase focus:border-primary-500 focus:ring-primary-500">

          <label class="flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" v-model="memoriser" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600">
            <span>
              {{ $t('connexion.memoriser') }}
              <span class="block text-xs text-gray-500">{{ $t('connexion.memoriserAide') }}</span>
            </span>
          </label>

          <button @click="verifier" :disabled="chargement || !saisieComplete"
                  class="w-full rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed">
            {{ chargement ? $t('connexion.verification') : $t('connexion.verifier') }}
          </button>

          <div class="space-y-2 border-t border-gray-200 pt-4 text-center text-sm">
            <p v-if="methode === 'email' && !modeSecours">
              <button @click="renvoyerCode" :disabled="attenteRenvoi > 0 || chargement"
                      class="font-medium text-primary-600 hover:text-primary-500 disabled:text-gray-400">
                {{ attenteRenvoi > 0 ? $t('connexion.renvoyerDans', { s: attenteRenvoi }) : $t('connexion.renvoyer') }}
              </button>
            </p>
            <p>
              <button @click="basculerSecours" class="font-medium text-primary-600 hover:text-primary-500">
                {{ modeSecours ? $t('connexion.utiliserAppli') : $t('connexion.utiliserSecours') }}
              </button>
            </p>
            <p v-if="codesSecoursRestants !== null && !modeSecours" class="text-xs text-gray-500">
              {{ $t('connexion.secoursRestants', { n: codesSecoursRestants }) }}
            </p>
            <p>
              <button @click="demanderRecuperation" :disabled="chargement"
                      class="text-xs text-gray-500 underline hover:text-gray-700">
                {{ methode === 'email'
                   ? $t('connexion.perduEmail')
                   : $t('connexion.perduTotp') }}
              </button>
            </p>
            <p>
              <button @click="recommencer" class="text-xs text-gray-500 hover:text-gray-700">
                {{ $t('connexion.revenirConnexion') }}
              </button>
            </p>
          </div>
        </div>
      </template>

      <!-- ════ ÉTAPE 3 — enrôlement obligatoire ═════════════════════════════════════════ -->
      <template v-else-if="etape === 'enroll'">
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <div class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <EnrolementMfa :mfa-token="mfaToken" @termine="aboutir" />
        </div>
        <p class="text-center">
          <button @click="recommencer" class="text-xs text-gray-500 hover:text-gray-700">
            {{ $t('connexion.revenirConnexion') }}
          </button>
        </p>
      </template>
    </div>
  </div>

  <!-- Modal de récupération de mot de passe -->
  <div v-if="showForgotPassword" class="fixed z-10 inset-0 overflow-y-auto" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="showForgotPassword = false"></div>
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
        <h3 class="text-lg leading-6 font-medium text-gray-900">{{ $t('connexion.oublieTitre') }}</h3>
        <p class="mt-2 text-sm text-gray-500">
          {{ userType === 'admin' ? $t('connexion.oublieAdmin') : $t('connexion.oublieClient') }}
        </p>
        <!-- Dit dès maintenant, parce que c'est la première question qui vient : non, le lien
             ne sert pas à contourner le second facteur. -->
        <p class="mt-2 text-xs text-gray-500">
          {{ $t('connexion.oublieMfa') }}
        </p>

        <div v-if="recoverySuccess" class="mt-3 rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
          {{ recoverySuccess }}
        </div>
        <div v-if="recoveryError" class="mt-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
          {{ recoveryError }}
        </div>

        <input v-model="recoveryEmail" type="email" placeholder="adresse@exemple.fr"
               class="mt-4 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500 sm:text-sm">

        <div class="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
          <button type="button" @click="sendRecoveryEmail" :disabled="recoveryLoading"
                  class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-60">
            {{ recoveryLoading ? $t('connexion.envoiEnCours') : $t('connexion.envoyer') }}
          </button>
          <button type="button" @click="showForgotPassword = false"
                  class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
            {{ $t('commun.annuler') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, reactive, ref, computed, nextTick, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import nextRoute from '@/services/next-route';
import deviceTrust from '@/services/device-trust';
import apiService from '@/services/api.service';
import EnrolementMfa from '@/components/auth/EnrolementMfa.vue';
import LangueSelect from '@/components/common/LangueSelect.vue';
import { useI18n } from 'vue-i18n';
import { texteErreur } from '@/i18n/serveur';

export default defineComponent({
  name: 'Login',
  components: { EnrolementMfa, LangueSelect },

  setup() {
    const store = useStore();
    const { t } = useI18n();

    const userType = ref('admin');
    const form = reactive({ username: '', email: '', password: '' });

    // 'identifiants' → 'mfa' | 'enroll' → session ouverte.
    const etape = ref('identifiants');
    const mfaToken = ref(null);
    const codesSecoursRestants = ref(null);

    const code = ref(''), codeSecours = ref(''), modeSecours = ref(false);
    const memoriser = ref(false), recuperation = ref(null);
    const methode = ref('totp'), envoi = ref(null), attenteRenvoi = ref(0);
    let minuteur = null;
    const chargement = ref(false), error = ref('');
    const champCode = ref(null);

    const showForgotPassword = ref(false);
    const recoveryEmail = ref(''), recoveryLoading = ref(false);
    const recoverySuccess = ref(''), recoveryError = ref('');

    // Décompte avant de pouvoir redemander un code. Le serveur refuse de toute façon avant
    // une minute ; ce n'est que pour éviter un bouton qui échoue sans rien expliquer.
    const lancerDecompte = (s) => {
      attenteRenvoi.value = s;
      clearInterval(minuteur);
      minuteur = setInterval(() => { if (--attenteRenvoi.value <= 0) clearInterval(minuteur); }, 1000);
    };
    onUnmounted(() => clearInterval(minuteur));

    const saisieComplete = computed(() => modeSecours.value
      ? codeSecours.value.replace(/[^A-Za-z0-9]/g, '').length >= 8
      : code.value.replace(/\D/g, '').length === 6);

    // ── Fin de parcours : la session s'ouvre ici, et nulle part ailleurs.
    const aboutir = async (data) => {
      if (!data?.token) { error.value = t('connexion.reponseInattendue'); return; }

      // Le jeton d'appareil est rangé par TYPE de compte : sur un poste partagé, la
      // mémorisation de l'administrateur ne doit pas écraser celle du client.
      if (data.deviceToken) deviceTrust.poser(userType.value, data.deviceToken);

      const user = await store.dispatch('auth/etablirSession', { token: data.token, user: data.user });

      // 🔴 `nextRoute` n'est PAS consommé quand on part vers le changement de mot de passe :
      // cet écran est encore intermédiaire, et c'est lui qui consommera la destination.
      if (user.firstLogin) { window.location.href = '/change-password'; return; }
      // Compte en cours de suppression (B9) : proposer d'annuler avant toute autre chose.
      if (data.suppression) { window.location.href = '/compte/suppression'; return; }
      window.location.href = nextRoute.consommer() || (user.role === 'admin' ? '/admin' : '/client');
    };

    // ── Étape 1
    const soumettreIdentifiants = async () => {
      chargement.value = true; error.value = '';
      try {
        const identifiants = userType.value === 'admin'
          ? { username: form.username, password: form.password }
          : { email: form.email, password: form.password };
        // Le jeton d'appareil est envoyé dès l'étape 1 : s'il est reconnu, le serveur répond
        // directement « done » et le second facteur n'est pas demandé.
        identifiants.deviceToken = deviceTrust.lire(userType.value);

        const { data } = userType.value === 'admin'
          ? await apiService.login(identifiants)
          : await apiService.loginClient(identifiants);

        if (data.step === 'done') return aboutir(data);

        mfaToken.value = data.mfaToken;
        codesSecoursRestants.value = data.codesSecoursRestants ?? null;
        methode.value = data.methode || 'totp';
        envoi.value = data.envoi || null;
        // Le code par courriel est parti tout seul à l'ouverture de l'étape : personne ne
        // comprendrait d'avoir à demander son propre code.
        if (data.envoi?.envoye) lancerDecompte(data.envoi.renvoiDansS || 60);
        etape.value = data.step; // 'mfa' ou 'enroll'
        if (data.step === 'mfa') nextTick(() => champCode.value?.focus());
      } catch (e) {
        // L'appareil n'est manifestement plus reconnu : on nettoie plutôt que de le renvoyer
        // à chaque tentative.
        if (e.response?.status === 401) deviceTrust.oublier(userType.value);
        error.value = texteErreur(e, t('connexion.erreurConnexion'));
      } finally {
        chargement.value = false;
      }
    };

    // ── Étape 2
    const verifier = async () => {
      if (!saisieComplete.value || chargement.value) return;
      chargement.value = true; error.value = '';
      try {
        const { data } = await apiService.mfaVerifier({
          mfaToken: mfaToken.value,
          code: modeSecours.value ? undefined : code.value.replace(/\D/g, ''),
          backupCode: modeSecours.value ? codeSecours.value : undefined,
          rememberDevice: memoriser.value
        });
        return aboutir(data);
      } catch (e) {
        error.value = texteErreur(e, t('connexion.verificationImpossible'));
        // Le jeton de passage a expiré : inutile de laisser l'utilisateur retaper des codes
        // dans le vide, on le ramène au début.
        if (e.response?.data?.code === 'PASSAGE_EXPIRE') recommencer(error.value);
        code.value = ''; codeSecours.value = '';
      } finally {
        chargement.value = false;
      }
    };

    const basculerSecours = () => {
      modeSecours.value = !modeSecours.value;
      code.value = ''; codeSecours.value = ''; error.value = '';
    };

    const renvoyerCode = async () => {
      chargement.value = true; error.value = '';
      try {
        const { data } = await apiService.mfaRenvoyerCode(mfaToken.value, 'login');
        envoi.value = data.envoi;
        lancerDecompte(data.envoi?.renvoiDansS || 60);
      } catch (e) {
        if (e.response?.data?.code === 'TROP_TOT') lancerDecompte(e.response.data.attente_s || 60);
        error.value = texteErreur(e, t('connexion.renvoiImpossible'));
      } finally {
        chargement.value = false;
      }
    };

    const demanderRecuperation = async () => {
      chargement.value = true; error.value = '';
      try {
        const { data } = await apiService.mfaRecuperationDemander(mfaToken.value);
        recuperation.value = data;
      } catch (e) {
        error.value = texteErreur(e, t('connexion.demandeImpossible'));
      } finally {
        chargement.value = false;
      }
    };

    const recommencer = (message = '') => {
      etape.value = 'identifiants';
      mfaToken.value = null; code.value = ''; codeSecours.value = '';
      modeSecours.value = false; recuperation.value = null;
      methode.value = 'totp'; envoi.value = null;
      clearInterval(minuteur); attenteRenvoi.value = 0;
      form.password = '';
      error.value = message;
    };

    const sendRecoveryEmail = async () => {
      if (!recoveryEmail.value) { recoveryError.value = t('connexion.adresseRequise'); return; }
      recoveryLoading.value = true; recoveryError.value = ''; recoverySuccess.value = '';
      try {
        // Le type de compte accompagne la demande : l'adresse seule ne dit pas s'il s'agit
        // d'un administrateur ou d'un client, et les deux tables sont distinctes.
        await apiService.forgotPassword(recoveryEmail.value, userType.value);
        recoverySuccess.value = t('connexion.recupEnvoyee');
        setTimeout(() => { showForgotPassword.value = false; recoveryEmail.value = ''; recoverySuccess.value = ''; }, 5000);
      } catch (e) {
        recoveryError.value = t('connexion.recupErreur');
      } finally {
        recoveryLoading.value = false;
      }
    };

    // La destination en attente est posée DÈS L'ARRIVÉE, et non à la soumission : un client
    // qui vient de recevoir sa carte n'a pas encore de compte, il passe par « Créer votre
    // compte » puis revient — et ce détour effaçait le `?next=` de l'URL, donc son code de
    // rattachement avec lui.
    const enAttente = nextRoute.depuisUrl();
    if (enAttente) nextRoute.poser(enAttente);

    // Retours des liens cliqués dans un courriel.
    const avisVerif = (() => {
      let p;
      try { p = new URLSearchParams(window.location.search); } catch (e) { return null; }
      const v = p.get('verifie'), r = p.get('recuperation');
      // `cle` plutôt que le texte : il est traduit à l'affichage, et suit un changement de langue.
      if (v === 'ok')     return { ok: true,  cle: 'connexion.avisVerifieOk' };
      if (v === 'expire') return { ok: false, cle: 'connexion.avisVerifieExpire' };
      if (v === 'erreur') return { ok: false, cle: 'connexion.avisVerifieErreur' };
      if (r === 'annulee')    return { ok: true,  cle: 'connexion.avisRecupAnnulee' };
      if (r === 'introuvable')return { ok: false, cle: 'connexion.avisRecupIntrouvable' };
      if (r === 'erreur')     return { ok: false, cle: 'connexion.avisRecupErreur' };
      return null;
    })();

    return {
      userType, form, etape, mfaToken, codesSecoursRestants,
      code, codeSecours, modeSecours, memoriser, recuperation,
      methode, envoi, attenteRenvoi, renvoyerCode,
      chargement, error, champCode, saisieComplete, avisVerif,
      soumettreIdentifiants, verifier, basculerSecours, demanderRecuperation,
      recommencer, aboutir,
      showForgotPassword, recoveryEmail, recoveryLoading, recoverySuccess, recoveryError, sendRecoveryEmail
    };
  }
});
</script>
